// @ts-nocheck
import React, { PropTypes } from 'react'
import { connect } from 'react-redux'
import { Toggle, TextField, RaisedButton, CircularProgress, Divider } from 'material-ui'
import { CardTitle } from 'material-ui/Card'
import { setSnackbarMessage } from '../../../actions/snackbar'
import api from '../../../api'
import appConfig from '../../../config'
import PageBlocks from '../../cms-page-blocks/list'

const sectionStyle: React.CSSProperties = {
  marginTop: '24px',
  marginBottom: '8px'
}

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '8px'
}

const fieldStyle: React.CSSProperties = {
  marginBottom: '8px'
}

const hintStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#999',
  marginTop: '2px',
  marginBottom: '12px'
}

const previewLinkStyle: React.CSSProperties = {
  display: 'inline-block',
  marginTop: '16px',
  fontSize: '13px',
  color: '#1a73e8'
}

interface LandingPageConfig {
  enabled?: boolean
  primary_color?: string
  text_color?: string
  button_color?: string
  abbreviated_name?: string
  show_institution_name?: boolean
  hero_headline?: string
  hero_subtitle?: string
  powered_by_text?: string
  activation_instructions?: string
  activation_link?: string
  upcoming_programs_subtitle?: string
  support_title?: string
  support_message?: string
}

interface State {
  loading: boolean
  saving: boolean
  institution: any
  config: LandingPageConfig
}

class WhitelabelConfig extends React.Component<any, State> {
  constructor (props) {
    super(props)
    this.state = {
      loading: true,
      saving: false,
      institution: null,
      config: {}
    }
  }

  componentWillMount () {
    this.loadData()
  }

  loadData () {
    return api.get(`admin/institutions/${this.props.params.institutionId}`)
      .then(res => res.json())
      .then(institution => {
        this.setState({
          loading: false,
          institution,
          config: institution.landing_page_config || {}
        })
      })
  }

  setConfig (key: keyof LandingPageConfig, value: any) {
    this.setState(s => ({ config: { ...s.config, [key]: value } }))
  }

  handleEnabledToggle (val: boolean) {
    const config = { ...this.state.config, enabled: val }
    this.setState({ config })
    api.patch(`admin/institutions/${this.props.params.institutionId}?with_deleted=true`, {
      body: { landing_page_config: config }
    })
      .then(res => res.json())
      .then(() => {
        this.props.setSnackbarMessage(`Whitelabel landing page ${val ? 'enabled' : 'disabled'}`)
      })
  }

  handleSave () {
    this.setState({ saving: true })
    api.patch(`admin/institutions/${this.props.params.institutionId}?with_deleted=true`, {
      body: { landing_page_config: this.state.config }
    })
      .then(res => res.json())
      .then(() => {
        this.setState({ saving: false })
        this.props.setSnackbarMessage('Whitelabel config saved')
      })
      .catch(() => {
        this.setState({ saving: false })
      })
  }

  render () {
    const { loading, saving, institution, config } = this.state
    const { params: { institutionId }, route } = this.props

    if (loading) return <CircularProgress />

    const membersBaseUrl = appConfig.webUrl.replace(/\/$/, '')
    const previewPath = `/institutions/${institution.slug}/hub`

    return (
      <div style={{ maxWidth: 700 }}>
        <CardTitle title='Whitelabel Landing Page' style={{ paddingLeft: 0 }} />

        {/* Master enable toggle */}
        <Toggle
          label='Enable Whitelabel Landing Page'
          labelPosition='right'
          toggled={!!config.enabled}
          onToggle={(_, val) => this.handleEnabledToggle(val)}
          style={{ marginBottom: '8px' }}
        />
        <p style={hintStyle}>
          When enabled, the landing page is accessible at{' '}
          <code>{previewPath}</code>.{' '}
          {institution.slug && (
            <a
              href={`${membersBaseUrl}${previewPath}`}
              target='_blank'
              rel='noopener noreferrer'
              style={previewLinkStyle}
            >
              Preview &rarr;
            </a>
          )}
        </p>

        {config.enabled && <div>
        <Divider style={{ margin: '16px 0' }} />

        {/* Branding */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Branding</div>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Primary Color (hex, e.g. #1a2e5c)'
            value={config.primary_color || ''}
            onChange={(_, val) => this.setConfig('primary_color', val)}
            fullWidth
          />
          <p style={hintStyle}>
            Used for the header/nav background and button color. Light colors (e.g. white) are
            detected automatically: the NCFDD logo, header text, and buttons switch to legible
            values so nothing disappears against a light background.
          </p>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Text Color Override (hex, optional)'
            value={config.text_color || ''}
            onChange={(_, val) => this.setConfig('text_color', val)}
            fullWidth
          />
          <p style={hintStyle}>
            When set, overrides the primary color for heading text throughout the page (section titles, card headers, etc.),
            and is used for header text and buttons when the primary color is too light to be readable.
            Leave blank to use the primary color for both the header background and text.
            If this color is itself too light for body text, headings fall back to the NCFDD navy.
          </p>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Button Color Override (hex, optional)'
            value={config.button_color || ''}
            onChange={(_, val) => this.setConfig('button_color', val)}
            fullWidth
          />
          <p style={hintStyle}>
            When set, overrides the color of the Activate and Login buttons only. Works without a text color.
            If it is too light for white button text, the buttons fall back to the primary or text color.
          </p>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Abbreviated Name'
            value={config.abbreviated_name || ''}
            onChange={(_, val) => this.setConfig('abbreviated_name', val)}
            fullWidth
          />
          <p style={hintStyle}>
            Short form of the institution name used in body copy (e.g. "MIT" instead of "Massachusetts Institute of Technology").
            Defaults to the full institution name if blank.
          </p>
        </div>

        <Toggle
          label='Show Institution Name in Header'
          labelPosition='right'
          toggled={config.show_institution_name !== false}
          onToggle={(_, val) => this.setConfig('show_institution_name', val)}
          style={{ marginTop: '8px', marginBottom: '4px' }}
        />
        <p style={hintStyle}>
          When disabled, only the logo image (if set) appears in the header. When no logo is uploaded this has no effect.
        </p>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Powered By Text'
            value={config.powered_by_text || ''}
            onChange={(_, val) => this.setConfig('powered_by_text', val)}
            fullWidth
          />
          <p style={hintStyle}>Text shown next to the NCFDD logo in the header. Defaults to "In partnership with".</p>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Hero banner */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Hero Banner</div>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Hero Headline'
            value={config.hero_headline || ''}
            onChange={(_, val) => this.setConfig('hero_headline', val)}
            multiLine
            rows={2}
            rowsMax={4}
            fullWidth
          />
          <p style={hintStyle}>
            Main heading on the landing page. Defaults to "NCFDD and [Institution] are excited to partner together…"
          </p>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Hero Subtitle'
            value={config.hero_subtitle || ''}
            onChange={(_, val) => this.setConfig('hero_subtitle', val)}
            multiLine
            rows={2}
            rowsMax={4}
            fullWidth
          />
          <p style={hintStyle}>
            Body text below the headline. Defaults to "As a partner institution, [Institution] provides access to NCFDD's complete library…"
          </p>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Activation card */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Activation Card</div>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Activation Instructions'
            value={config.activation_instructions || ''}
            onChange={(_, val) => this.setConfig('activation_instructions', val)}
            multiLine
            rows={2}
            rowsMax={5}
            fullWidth
          />
          <p style={hintStyle}>
            Appears below "Activate Your NCFDD Membership". Defaults to "Activate using your institutional email address."
          </p>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Activation Link URL'
            value={config.activation_link || ''}
            onChange={(_, val) => this.setConfig('activation_link', val)}
            fullWidth
          />
          <p style={hintStyle}>
            Override the default activation button URL. Defaults to /institutions/[slug]/register.
          </p>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Upcoming Events */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Upcoming Events</div>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Upcoming Programs & Courses Subtitle'
            value={config.upcoming_programs_subtitle || ''}
            onChange={(_, val) => this.setConfig('upcoming_programs_subtitle', val)}
            fullWidth
          />
          <p style={hintStyle}>
            Subtitle shown under "Upcoming Programs &amp; Courses". Defaults to "Multi-week programs and intensive courses for faculty at all career stages."
          </p>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Footer */}
        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Footer / Support Message</div>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Footer Header'
            value={config.support_title || ''}
            onChange={(_, val) => this.setConfig('support_title', val)}
            fullWidth
          />
          <p style={hintStyle}>Heading shown in the footer section. Defaults to "We&rsquo;re Here to Support You".</p>
        </div>

        <div style={fieldStyle}>
          <TextField
            floatingLabelText='Support Message'
            value={config.support_message || ''}
            onChange={(_, val) => this.setConfig('support_message', val)}
            multiLine
            rows={2}
            rowsMax={5}
            fullWidth
          />
          <p style={hintStyle}>
            Shown below the footer heading. Defaults to: &ldquo;Questions? Contact your Faculty Development office or reach out to NCFDD at members@ncfdd.org.&rdquo;
          </p>
        </div>

        <div style={{ marginTop: '24px' }}>
          <RaisedButton
            label='Save'
            primary
            onClick={() => this.handleSave()}
            labelPosition='before'
            icon={saving ? <CircularProgress size={20} thickness={2} /> : null}
            disabled={saving}
          />
        </div>

        <Divider style={{ margin: '32px 0 16px' }} />

        <div style={sectionStyle}>
          <div style={sectionHeadingStyle}>Content Blocks</div>
        </div>
        <p style={hintStyle}>
          Add curated webinar sections or custom HTML to display on the landing page, in the order configured below.
        </p>
        <p style={hintStyle}>
          The <code>Default*</code> block types insert NCFDD's default content and stay in sync with it. <code>DefaultHtml</code> uses the{' '}
          <a href='/settings/default_institution_html_block' target='_blank' rel='noopener noreferrer'>Default Institution HTML Block</a> setting.
          With no blocks added, all three defaults are shown.
        </p>

        <div style={{ padding: '24px 0 48px' }}>
          <PageBlocks
            title=''
            target='institution_page_blocks'
            cmsPageProp='institution_id'
            cmsPageId={institutionId}
            route={route}
            allowedBlockTypes={['CuratedWebinarSection', 'GenericHtml', 'DefaultCuratedByRole', 'DefaultCuratedByTheme', 'DefaultHtml']}
          />
        </div>
        </div>}
      </div>
    )
  }
}

WhitelabelConfig.propTypes = {
  params: PropTypes.shape({ institutionId: PropTypes.string.isRequired }).isRequired,
  setSnackbarMessage: PropTypes.func.isRequired
}

export default connect(null, { setSnackbarMessage })(WhitelabelConfig)
